import React, { useEffect } from "react";
import { FaCheck } from "react-icons/fa";
import { ImCross } from "react-icons/im";

import { ReviewExamBody, UserExamApi, userExamsApi } from "api/userExamsApi";
import { ReviewAnswerPayload, useReviewExam } from "./store/reducer";
import { RatingFromValues, RatingPanelForm } from "./RatingPanelForm";
import { ReviewSummary } from "./ReviewSummary";
import { useToaster } from "utils";
import { useNavigate } from "react-router-dom";
import { routes } from "consts";
import { EXAM_STATUS, ExamStatusIds } from "consts";
import { appendFileToBrowser } from "utils";

interface ExamPreviewProps {
  userExam: UserExamApi;
  openAnswer: number;
  setOpenAnswer: (idx: number) => void;
  onClick: (idx: number) => void;
  onSubmit: (values: RatingFromValues) => Promise<void>;
  courseId: number;
  courseExamId: number;
  userId: number;
  userExamId: number;
}
export const ExamPreview = ({
  userExam,
  openAnswer,
  setOpenAnswer,
  onClick,
  onSubmit,
  courseId,
  courseExamId,
  userId,
  userExamId,
}: ExamPreviewProps) => {
  const {
    dispatch,
    state: { answers },
  } = useReviewExam();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToaster();

  useEffect(() => {
    dispatch({ type: "INITIALIZE_EXAM", payload: userExam });
  }, []);

  const onSubmitHandler = async (values: ReviewAnswerPayload) => {
    dispatch({ type: "REVIEW_ANSWER", payload: values });
    onSubmit(values);
  };

  const onSummarySubmit = async (formData: ReviewExamBody) => {
    try {
      await userExamsApi.reviewUserExam(courseId, courseExamId, userId, userExamId, formData);
      showSuccess("Egzamin został oceniony");
      navigate(routes.COURSES_MANAGE_NESTED.EXAMS(courseId, courseExamId));
    } catch {
      showError("Błąd podczas oceny egzaminu");
      console.error("Error while submitting review");
    }
  };

  const downloadFile = async (fileId: number, originalFileName: string) => {
    try {
      const response = await userExamsApi.downloadFile(courseId, courseExamId, userId, userExamId, fileId);
      appendFileToBrowser(response.data, originalFileName);
    } catch (e) {
      showError("Błąd podczas pobierania pliku");
      console.error("Error while downloading file", e);
    }
  };

  return (
    <div className="col-span-12 grid grid-cols-5 gap-4 divide-x-2 rounded-sm border border-slate-200 bg-white p-4 shadow-lg dark:border-slate-700 dark:bg-slate-800">
      <div className="col-span-3">
        <div className="text-center text-lg font-semibold">Odpowiedzi kursanta</div>
        {/* TODO: Responsywność */}
        <div>
          <div>
            {answers.map((answer, idx) => (
              <Row key={answer.userExamAnswerId} onClick={() => onClick(idx)} isOpen={openAnswer !== idx}>
                <div className="flex justify-between">
                  <div>
                    <span className="font-semibold">Pytanie {idx + 1} </span>
                    {answer.question.question}
                  </div>
                  <div>
                    {answer.review?.points ? (
                      <span className="text-primary-500 font-semibold">{answer.review.points}/5</span>
                    ) : !answer.review || answer.review.passed == null ? null : answer.review.passed ? (
                      <FaCheck color="green" />
                    ) : (
                      <ImCross color="red" />
                    )}
                  </div>
                </div>
                {openAnswer === idx &&
                  (answer.value ? (
                    <div>
                      {answer.question.inputType === "free-text" && answer.value}

                      {["single-select", "multi-select"].includes(answer.question.inputType) &&
                        answer.question.options?.map((option) => (
                          <div key={option.id}>
                            <input type="radio" checked={answer.value == option.id} disabled />
                            <span>{option.value}</span>
                          </div>
                        ))}
                      {answer.files.length > 0 && (
                        <div className="text-sm">
                          <span className="font-semibold">Lista plików</span>
                          {answer.files.map((file) => (
                            <div
                              key={file.id}
                              className="cursor-pointer hover:underline"
                              onClick={() => downloadFile(file.id, file.originalFileName)}
                            >
                              &ndash; {file.originalFileName}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="italic text-orange-400">Brak odpowiedzi</div>
                  ))}
              </Row>
            ))}
            <Row isOpen={openAnswer !== answers.length} onClick={() => setOpenAnswer(answers.length)}>
              <span className="font-semibold">Podsumowanie</span>
            </Row>
          </div>
        </div>
      </div>
      <div className="col-span-2 pl-4">
        <div className="text-center text-lg font-semibold">Panel oceny</div>
        {answers.map((answer, idx) => (
          <RatingPanelForm
            key={answer.userExamAnswerId}
            answer={answer}
            onSubmit={onSubmitHandler}
            hidden={openAnswer !== idx}
          />
        ))}
        {openAnswer === answers.length && (
          <ReviewSummary
            onSubmit={onSummarySubmit}
            defaultValues={{
              passed: ([EXAM_STATUS.PASSED, EXAM_STATUS.PASSED_NOT_PUBLISHED] as ExamStatusIds[]).includes(
                userExam.status,
              ),
              comment: userExam.reviewComment,
            }}
          />
        )}
      </div>
    </div>
  );
};

interface RowProps {
  onClick: () => void;
  isOpen: boolean;
  children: React.ReactNode;
}
const Row = ({ onClick, isOpen, children }: RowProps) => {
  return (
    <div
      onClick={onClick}
      className={`m-2 border p-2 ${
        isOpen &&
        "bg-slate-50 opacity-35 hover:cursor-pointer hover:bg-white hover:opacity-100 dark:bg-slate-900 dark:hover:bg-slate-600"
      }`}
    >
      {children}
    </div>
  );
};
